import { inject, Injectable } from '@angular/core';
import { CredentialsCurrentUser } from '@app/core';
import {
    HomePageCarouselBannerService as HomePageCarouselItemService,
    HomePageService as HomePageWallItemService,
    HomePageBottomBannerService as HomePageBannerItemService
} from '@app/core/api/services';
import {
    HomePageCarouselBanner,
    HomePage,
    HomePageBottomBanner
} from '@app/core/services/api/API.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { sortJSON } from '../../util.service';
import { ROLES_GROUPS } from '@app/constants';

@Injectable({
    providedIn: 'root'
})
export class HomePageService {
    private _homePageCarouselItems$: BehaviorSubject<
        HomePageCarouselBanner[] | null
    > = new BehaviorSubject(null);
    private _homePageWallItems$: BehaviorSubject<
        HomePage[] | null
    > = new BehaviorSubject(null);
    private _homePageBannerItems$: BehaviorSubject<
        HomePageBottomBanner[] | null
    > = new BehaviorSubject(null);

    private _isLoading = false;
    private _lastLoadedUser: CredentialsCurrentUser | null = null;

    private _homePageCarouselItemService = inject(HomePageCarouselItemService);
    private _homePageWallItemService = inject(HomePageWallItemService);
    private _homePageBannerItemService = inject(HomePageBannerItemService);

    get homePageCarouselItems$(): Observable<HomePageCarouselBanner[]> {
        return this._homePageCarouselItems$.asObservable();
    }

    get homePageWallItems$(): Observable<HomePage[]> {
        return this._homePageWallItems$.asObservable();
    }

    get homePageBannerItems$(): Observable<HomePageBottomBanner[]> {
        return this._homePageBannerItems$.asObservable();
    }

    /**
     * Actualizar los banners del carrusel (para usuarios no autenticados)
     */
    setCarouselBanners(banners: HomePageCarouselBanner[]): void {
        this._homePageCarouselItems$.next(banners);
    }

    /**
     * Actualizar los wall items (para usuarios no autenticados)
     */
    setWallItems(wallItems: HomePage[]): void {
        this._homePageWallItems$.next(wallItems);
    }

    /**
     * Actualizar los banners del banner (para usuarios no autenticados)
     */
    setHomePageBannerItems(homePageBannerItems: HomePageBottomBanner[]): void {
        this._homePageBannerItems$.next(homePageBannerItems);
    }

    async listHomePageLists(currentUser: CredentialsCurrentUser) {
        if (this._isLoading) {
            return;
        }

        if (
            this._lastLoadedUser &&
            this._lastLoadedUser.id === currentUser.id &&
            this._lastLoadedUser.role === currentUser.role &&
            this._homePageWallItems$.getValue() !== null
        ) {
            return;
        }

        this._isLoading = true;
        try {
            let filter = null;
            if (ROLES_GROUPS.MKT_GROUP.includes(currentUser.role)) {
                filter = {
                    status: { eq: true },
                    visibleFor: { contains: currentUser.clientId }
                };
                let [
                    homePageCarouselItems,
                    homePageWallItems,
                    homePageBannerItems
                ] = await Promise.all([
                    this._homePageCarouselItemService.listHomePageCarouselBanners(
                        filter
                    ),
                    this._homePageWallItemService.listHomePages(filter),
                    this._homePageBannerItemService.listHomePageBottomBanners(
                        filter
                    )
                ]);
                this._homePageCarouselItems$.next(
                    sortJSON(homePageCarouselItems, 'position', 'asc')
                );

                if (currentUser.brandId) {
                    homePageWallItems = homePageWallItems.filter(
                        (item: HomePage) => {
                            const shouldInclude =
                                !item.visibleForUserBrand ||
                                item.visibleForUserBrand.length === 0 ||
                                (item.visibleForUserBrand.length > 0 &&
                                    item.visibleForUserBrand.includes(
                                        currentUser.brandId
                                    ));
                            return shouldInclude;
                        }
                    );
                } else {
                    homePageWallItems = homePageWallItems.filter(
                        (item: HomePage) => {
                            const shouldInclude =
                                !item.visibleForUserBrand ||
                                item.visibleForUserBrand.length === 0;
                            return shouldInclude;
                        }
                    );
                }

                this._homePageWallItems$.next(
                    sortJSON(homePageWallItems, 'position', 'asc')
                );
                this._homePageBannerItems$.next(homePageBannerItems);
            } else {
                this._homePageCarouselItems$.next([]);
                this._homePageWallItems$.next([]);
                this._homePageBannerItems$.next([]);
            }
            this._lastLoadedUser = { ...currentUser };
        } catch (err) {
            this._homePageCarouselItems$.next([]);
            this._homePageWallItems$.next([]);
            this._homePageBannerItems$.next([]);
        } finally {
            this._isLoading = false;
        }
    }
}
